import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import find from 'lodash/find';

import { MouseCatcher } from '../Catchers/mouseCatcher';
import { ScrollCatcher } from '../Catchers/scrollCatcher';
import { ContentModel, IModelOptions } from './contentModel';
import { ContentLight, ILightOptions } from './contentLight';
import { ContentAnimation } from './contentAnimation';
import { ContentsTrigger } from './contentsTrigger';
import { IContentOptions, ContentType } from './content';
import { SystemLayer } from '../System/systemLayer';

export const DEFAULT_MODEL_OPTION = {
    position: new Vector3(0, 0.5, 0),
    rotation: Vector3.Zero(),
    scale: Vector3.One(),
    type: 'model',
    visible: true,
};

export class ContentsManager extends ContentsTrigger {
    public _system: SystemLayer;

    constructor(system: SystemLayer, mouseCatcher: MouseCatcher, scrollCatcher?: ScrollCatcher) {
        super(mouseCatcher, scrollCatcher);
        this._system = system;
    }

    public setContents(contents: any): void {
        contents.forEach((content) => {
            this.addNewContent(content.type, content.contentdata, content.typedata);
        });
        this.hideContents();
    }

    public addContentAnimation(id: string, animations: any): void {
        const content = find(this.list, (o) => o.id === id);
        if (content) {
            content.addAnimations(animations);
            if (content.play) content.startAllAnimation();
            else content.pauseAnimation();
        }
    }

    public addContentVariants(id: string, variants: any): void {
        const content = find(this.list, (o) => o.id === id) as ContentModel;
        if (content) {
            content.setVariants(variants);
        }
    }

    public listenEvent(bool: boolean): void {
        const models = this.getContentModels();
        models.forEach((content) => {
            content.pattern.listenEvent(bool);
        });
    }

    public showDecals(bool: boolean): void {
        const models = this.getContentModels();
        models.forEach((content) => {
            content.showDecals(bool);
        });
    }

    public stopContents(): void {
        this.list.forEach((content) => {
            content.pauseAnimation();
        });
    }

    public hideContents(): void {
        this.list.forEach((content) => {
            content.hide();
        });
    }

    public showContents(): void {
        this.list.forEach((content) => {
            if (content.visible) content.show();
            else content.hide();
        });
    }

    public showLights(): void {
        const lights = this.getContentLights();
        lights.forEach((light) => {
            if (light.visible) light.show();
        });
    }

    public playContent(play: boolean): void {
        this.list.forEach((content) => {
            content.setPlay(play);
        });
        this.checkIfAnimations();
    }

    // Check which content can be optimize to improve scene performance
    public checkOptimization(): void {
        this.list.forEach((content) => {
            if (Object.keys(content.animations).length === 0) {
                // Refresh Mesh Scene info
                // content.pattern.unOptimize();
                content.pattern.optimize();
            }
        });
    }

    public addContent(content: ContentAnimation): void {
        this._addContent(content);
    }

    public removeContent(content: ContentAnimation): void {
        this._removeContent(content);
    }

    public addNewContent(
        type: ContentType,
        content: IContentOptions,
        typeStyle: any
    ): ContentAnimation {
        let newcontent: ContentAnimation;
        switch (type) {
            case 'model':
                newcontent = new ContentModel(this._system, content, typeStyle);
                break;
            case 'light':
                newcontent = new ContentLight(this._system, content, typeStyle);
                break;
            default:
                break;
        }
        this.addContent(newcontent);
        return newcontent;
    }

    public addModel(content: IContentOptions, type: IModelOptions): ContentModel {
        return this.addNewContent('model', content, type) as ContentModel;
    }

    public addLight(content: IContentOptions, type: ILightOptions): ContentLight {
        return this.addNewContent('light', content, type) as ContentLight;
    }

    public destroyContents(): void {
        this.list.forEach((content) => {
            content.pattern.destroy();
        });
        this.list = [];
    }

    public destroyModels(): void {
        const models = this.getContentModels();
        models.forEach((content) => {
            content.pattern.destroy();
            this.removeContent(content);
        });
    }
}
